import { useContext } from 'react';

import { Box, Flex, Progress } from '@chakra-ui/react';

import { DealStateEnum } from '../../../gql/dealGql';
import {
  DocumentProgressStatus,
  useOnDocumentProgressStatusSubscription,
} from '../../../gql/generated/graphql';

import SendWithAdobeSign from '../../../components/DealInfoBuyout/components/buttons/SendWithAdobeSign';
import GenerateDocumentsButton from '../../../components/GenerateDocs/GenerateDocumentsButton';
import Card from '../../../components/shared/Card';
import CardHeaderV2 from '../../../components/shared/Card/components/CardHeaderV2';
import MoveToButton from '../ProgressionButtons/MoveToButton';

import { NonButtonStep } from './NonButtonStep';
import TimelineIcon from './TimelineIcon';

import { DealActionsEnum, DealContext } from '../../../libs/DealContext';
import {
  adobeDocumentProgressStatusFromString,
  dealStateFromString,
} from '../../../utils/dealStates';

const AdobeContractStatusTimeline = () => {
  const { deal, dispatch } = useContext(DealContext);

  const width = '100px';

  const getAdobeTimelineStep = (status?: DocumentProgressStatus | null) => {
    switch (status) {
      case DocumentProgressStatus.UploadDocuments:
      case DocumentProgressStatus.ReadyForSignatures: // Should never happen for Adobe but just in case.
        return 2;
      case DocumentProgressStatus.SentForSignatures:
        return 3;
      case DocumentProgressStatus.Signed:
        return 4;
      case DocumentProgressStatus.GenerateDocuments:
      default:
        return 1;
    }
  };

  const timelineStep = getAdobeTimelineStep(deal.document_progress_status);

  useOnDocumentProgressStatusSubscription({
    variables: {
      deal_id: deal.id,
    },
    fetchPolicy: 'network-only',
    skip: !deal.id,
    onData: async ({ data }) => {
      const updatedDeals = data.data?.onDocumentProgressStatus?.filter((d) => d != null) ?? [];
      const updatedDeal = updatedDeals.find((d) => String(d.id) === String(deal.id));
      if (!updatedDeal) {
        return;
      }

      dispatch({
        type: DealActionsEnum.DeepUpdateDeal,
        payload: {
          state: dealStateFromString(updatedDeal.state) ?? deal.state,
          document_progress_status:
            adobeDocumentProgressStatusFromString(updatedDeal.document_progress_status) ??
            deal.document_progress_status,
        },
      });
    },
  });

  return (
    <Card variant="rounded" pb={0}>
      <CardHeaderV2 title="Contract Status" variant="rounded" pod={deal.pod} showPodColor />
      <Flex overflowX="auto">
        <Box minW="580px" mb={4} w="full">
          <Flex margin={5} justifyContent="space-between">
            <GenerateDocumentsButton useMiniButton width={width} />
            <SendWithAdobeSign width={width} inContractStatusTimeline />
            <NonButtonStep width={width}>SIGNING</NonButtonStep>
            <MoveToButton newState={DealStateEnum.Signed} width={width} inContractStatusTimeline />
          </Flex>
          <Flex
            marginX="60px"
            marginY="0px"
            justifyContent="space-between"
            alignItems="center"
            height={8}
            textAlign="center"
          >
            {[1, 2, 3, 4].map((step) => (
              <TimelineIcon key={step} position={step} step={timelineStep} lastStep={4} />
            ))}
          </Flex>
          <Progress
            margin="-18px 60px 25px 65px"
            height="5px"
            colorScheme="caribbeanGreen"
            bgColor="black"
            value={(timelineStep - 1) * (100 / 3)} // Each step is 1/3 of the progress bar
          />
        </Box>
      </Flex>
    </Card>
  );
};

export default AdobeContractStatusTimeline;
